<script setup>
/*
	Modal for showing the results of the User Invite and User Org uploads b/c they have so many failure points.
 */
import BaseModal from '@/components/Alerts/BaseModal.vue'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'

import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import BasicTableViewer from '@/components/Alerts/BasicTableViewer.vue'

const props = defineProps({
	show: { type: Boolean, default: false },//Show modal
	title: { type: String, default: 'Upload Results' },
	loader: { type: String, default: '' }, //Text in () after title with three bouncing dots for fun
	results : { type: Object, default: () => ({
		'errors' : [{ 'heading': "", 'items': [] }],
		'warnings' : [{ 'heading': "", 'items': [] }],
		'successes' : [{ 'heading': "", 'items': [] }],
		})
	},
	modalError: { type: Object, default: () => ({ 'error_eh': false, 'error_msg': 'There was an error loading tags. Please try reloading the page.' }) },
})

const emit = defineEmits(['evCloseModal'])//Used by ManageDocumentsPage to close modal

//#region Modal
/**
 * Check if there's any changes, if not, proceed with closing the modal
 * @param p_reload_eh True to reload the upload grid
 */
function dismissModal(p_reload_eh)
{
	closeModal(p_reload_eh)
}

/**
 * Do cleanup, show success toast and tell the UploadDocumentPage to close the modal
 * @param p_reload_eh
 */
function closeModal(p_reload_eh)
{
	//if (p_reload_eh) toaster.success("Your document has been successfully added.")
	emit('evCloseModal', 'upload_results', p_reload_eh)
}

//#endregion Modal

</script>

<template>
		<BaseModal :show="show" border-classes="border-t-6 border-tertiary">
			<div>
				<!-- Title/Close Button -->
				<div class="flex justify-between items-center border-b border-neutral w-full pb-4">
					<div class="flex flex-row text-xl font-bold text-heading">
						<div class="mr-2">{{ title }}</div>
						<div v-if="loader.length > 0" class="flex flex-row ">
							<div class="mr-2">({{ loader }}</div>
							<div class='flex flex-row space-x-2 justify-center items-center bg-white dark:invert pt-3'>
								<div class='h-1 w-1 bg-black rounded-full animate-bounce [animation-delay:-0.3s]'></div>
								<div class='h-1 w-1 bg-black rounded-full animate-bounce [animation-delay:-0.15s]'></div>
								<div class='h-1 w-1 bg-black rounded-full animate-bounce'></div>
							</div>
							<div>)</div>
						</div>
					</div>
					<img src="@/assets/images/icon/dismiss.svg" alt="Dismiss" title="Dismiss" class="cursor-pointer size-4" @click.prevent="dismissModal(false)">
				</div>

				<div class="w-136 max-h-[calc(100vh_-_11.125rem)] max-w-[calc(100vw_-_8.125rem)]  overflow-y-scroll">
					<div v-for="result in props.results.successes" :key="result.heading" class="mt-2" >
						<label class="font-bold text-success">{{ result.heading }}</label>
						<BasicTableViewer
							v-if="result['type'] && result.type==='table'"
							:data="result.items"
							:columns="result.columns"
							:modal="true"
							class="w-full"></BasicTableViewer>

						<ul v-else class="list-disc">
							<!-- item is an alias for the element being iterated on -->
							<li v-for="item in result.items" :key="item.id" class="ml-4">
								<span v-if="item.label" class="font-semibold mr-2">{{ item.label }}</span>
								<span>{{ item.text }}</span>
							</li>
						</ul>
					</div>

					<div v-for="result in props.results.warnings" :key="result.heading" class="mt-2" >
						<label class="font-bold text-lo-warning">{{ result.heading }}</label>
						<BasicTableViewer
							v-if="result['type'] && result.type==='table'"
							:data="result.items"
							:columns="result.columns"
							:modal="true"
							class="w-full"></BasicTableViewer>

						<ul v-else class="list-disc">
							<!-- item is an alias for the element being iterated on -->
							<li v-for="item in result.items" :key="item.id" class="ml-4">
								<span v-if="item.label" class="font-semibold mr-2">{{ item.label }}</span>
								<span>{{ item.text }}</span>
							</li>
						</ul>
					</div>

					<div v-for="result in props.results.errors" :key="result.heading" class="mt-2" >
						<label class="font-bold text-error">{{ result.heading }}</label>

						<BasicTableViewer
							v-if="result['type'] && result.type==='table'"
							:data="result.items"
							:columns="result.columns"
							:modal="true"
							class="w-full"></BasicTableViewer>

						<ul v-else class="list-disc">
							<!-- item is an alias for the element being iterated on -->
							<li v-for="item in result.items" :key="item.id" class="ml-4">
								<span v-if="item.label" class="font-semibold mr-2">{{ item.label }}</span>
								<span>{{ item.text }}</span>
							</li>
						</ul>
					</div>
				</div>
			</div>
			<form>
				<!-- Save/Cancel -->
				<div class="flex w-full flex-col-reverse sm:flex-row mt-4 sm:gap-y-1 gap-y-1">
					<div class="sm:mr-auto"></div>
					<SubmitButton label="Close" :type="ButtonType.Outline" :size="ButtonSize.Small" @click.prevent="dismissModal(false)"
												class="sm:mr-2" />

				</div>
			</form>
		</BaseModal>
</template>

<style scoped>

</style>